// dependencies
import React from "react";

import { faArrowDown, faArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import SelectInput from "../../../../../../components/Inputs/SelectInput";

// schema
import { SummaryFunction } from "../../../../schemas/report";

// styles
import css from "./ReportTable.module.scss";

interface ColumnSort {
	colId: string;
	sort: "asc" | "desc" | null;
	sortIndex: number;
}
interface BaseProps {
	isSummarizeRowsView: boolean;
	summaryFunction?: string;
	onInputChange: (value: string, selectedColumn: any) => void;
	displayName: string;
	selectedColumn: {
		field: string;
		fieldValueType: string;
	};
	availableSummaryFunctions: SummaryFunction[];
	columnSort: ColumnSort;
	allSorts: any;
	handleSort: (sorts: ColumnSort[]) => void;
}

const optionsLookup = {
	average: "Average",
	count: "Count",
	maximum: "Maximum",
	minimum: "Minimum",
	sum: "Sum",
	year: "Year",
	month: "Month",
	quarter: "Quarter",
	automatic: "Automatic",
	5: "5 bins",
	10: "10 bins",
	25: "25 bins",
};

const SelectSummaryFunction = (props: BaseProps) => {
	const {
		isSummarizeRowsView,
		summaryFunction,
		onInputChange,
		displayName,
		selectedColumn,
		columnSort,
		allSorts,
		availableSummaryFunctions,
		handleSort,
	} = props;

	const summaryFunctionOptions = availableSummaryFunctions.map((item) => {
		return {
			value: item,
			label: optionsLookup[item] || item,
		};
	});

	const getNextSortDirection = () => {
		switch (sortDirection) {
			case null:
				return "asc";
			case "asc":
				return "desc";
			default:
				return null;
		}
	};

	const getSortIndex = (shiftHeld) => {
		// set default sort index to 0
		let sort = 0;
		if (shiftHeld && sortIndex !== null) {
			sort = sortIndex;
		} else if (shiftHeld && sortIndex === null) {
			sort = allSorts.length;
		}
		return sort;
	};

	const sortDirection = columnSort?.sort || null;
	const sortIndex = columnSort?.sortIndex === undefined ? null : columnSort?.sortIndex;

	const handleSortChange = (event) => {
		const shiftHeld = event.shiftKey;
		const filteredSorts = allSorts.filter((item) => item.colId !== selectedColumn.field);

		handleSort([
			...(shiftHeld ? filteredSorts : []),
			{
				colId: selectedColumn.field,
				sort: getNextSortDirection(),
				sortIndex: getSortIndex(shiftHeld),
			},
		]);
	};

	let sortIcon;

	if (sortDirection === "asc") {
		sortIcon = <FontAwesomeIcon icon={faArrowUp} className={css.sortIcon} />;
	} else if (sortDirection === "desc") {
		sortIcon = <FontAwesomeIcon icon={faArrowDown} className={css.sortIcon} />;
	}

	return (
		<div className={css.summaryHeader}>
			<div className={`${css.customHeaderLabel}`} onClick={(event) => handleSortChange(event)}>
				<span className={css.text}>{displayName}</span>
				{sortIndex !== null && allSorts.length > 1 ? (
					<span className={css.sortIndex}>{sortIndex + 1}</span>
				) : null}
				{sortIcon}
			</div>
			{/* show summary function dropdown if we are in summarize view and column is numeric type */}
			{isSummarizeRowsView && summaryFunction && summaryFunctionOptions.length > 1 ? (
				<div className={css.customHeaderSelector}>
					<SelectInput
						placeholder=""
						value={summaryFunction}
						results={summaryFunctionOptions}
						onChange={(val) => onInputChange(val, selectedColumn)}
						menuPortalTarget={document.querySelector("body")}
					/>
				</div>
			) : null}
		</div>
	);
};

export default SelectSummaryFunction;
